import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Package, ClipboardCheck, FileBadge, TrendingUp, AlertTriangle, Clock } from "lucide-react";
import { Link } from "react-router-dom";

const KpiCard = ({ label, value, icon: Icon, accent, testid }) => (
  <div className="bg-white border border-gray-200 p-5" data-testid={testid}>
    <div className="flex items-start justify-between mb-3">
      <span className="text-xs uppercase tracking-wider font-semibold text-gray-500">{label}</span>
      <div className={`w-8 h-8 flex items-center justify-center ${accent}`}>
        <Icon className="w-4 h-4 text-white" strokeWidth={2.5} />
      </div>
    </div>
    <div className="font-display text-4xl font-bold tracking-tight">{value}</div>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="p-6 md:p-8" data-testid="dashboard-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Overview</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Dashboard</h1>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Total Assets" value={stats?.total_assets ?? "—"} icon={Package} accent="bg-[#01ADEF]" testid="kpi-total-assets" />
        <KpiCard label="Upcoming Inspections" value={stats?.upcoming_inspections ?? "—"} icon={ClipboardCheck} accent="bg-[#0284C7]" testid="kpi-upcoming" />
        <KpiCard label="Expired Certificates" value={stats?.expired_certs ?? "—"} icon={AlertTriangle} accent="bg-[#DC2626]" testid="kpi-expired" />
        <KpiCard label="Pass Rate" value={stats ? `${stats.pass_rate}%` : "—"} icon={TrendingUp} accent="bg-[#059669]" testid="kpi-passrate" />
      </div>

      {/* Second row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Alerts */}
        <div className="lg:col-span-2 bg-white border border-gray-200">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="font-display text-lg font-bold tracking-tight">Certification Alerts</h2>
              <p className="text-xs text-gray-500 mt-0.5">Expired or expiring within 30 days</p>
            </div>
            <AlertTriangle className="w-5 h-5 text-[#D97706]" />
          </div>
          <div className="divide-y divide-gray-100" data-testid="alerts-list">
            {stats?.alerts?.length ? stats.alerts.map((a) => (
              <div key={a.cert_id} className="px-5 py-3 flex items-center justify-between hover:bg-gray-50">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`inline-block w-2 h-2 ${a.type === 'expired' ? 'bg-[#DC2626]' : 'bg-[#D97706]'}`} />
                    <span className="font-semibold text-sm truncate">{a.asset_name || 'Unknown Asset'}</span>
                    <span className="font-mono text-xs text-gray-500">{a.asset_tag}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 font-mono">Cert #{a.certificate_number}</div>
                </div>
                <div className={`text-xs font-bold px-2 py-1 uppercase tracking-wider border ${
                  a.type === 'expired' ? 'bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]' : 'bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]'
                }`}>
                  {a.type === 'expired' ? `Expired ${Math.abs(a.days)}d ago` : `${a.days}d left`}
                </div>
              </div>
            )) : (
              <div className="px-5 py-10 text-center text-sm text-gray-500">No alerts — all certificates are valid.</div>
            )}
          </div>
        </div>

        {/* Cert status donut-ish */}
        <div className="bg-white border border-gray-200">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="font-display text-lg font-bold tracking-tight">Certificate Status</h2>
            <p className="text-xs text-gray-500 mt-0.5">Portfolio breakdown</p>
          </div>
          <div className="p-5 space-y-4">
            <StatusRow color="bg-[#059669]" label="Valid" count={stats?.valid_certs ?? 0} />
            <StatusRow color="bg-[#D97706]" label="Expiring Soon" count={stats?.expiring_soon ?? 0} />
            <StatusRow color="bg-[#DC2626]" label="Expired" count={stats?.expired_certs ?? 0} />
            <Link to="/certificates" className="block mt-6 text-xs uppercase tracking-wider font-semibold text-[#01ADEF] hover:text-[#0198D6]" data-testid="view-all-certs">
              View all certificates →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const StatusRow = ({ color, label, count }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-3">
      <span className={`w-3 h-3 ${color}`} />
      <span className="text-sm text-gray-700">{label}</span>
    </div>
    <span className="font-display font-bold text-xl">{count}</span>
  </div>
);
